/**
 * Entry intent Zustand store — persisted to platformStorage (AsyncStorage / localStorage).
 *
 * Set when a user arrives via a known onboarding entry point (org link, squad
 * invite, guest button, or cold "Get Started"). Read by TutorialScreen to
 * branch slide sets and by post-auth navigation to land the user on the right
 * destination. Cleared on sign-out (see SupabaseAuthContext) and auto-expired
 * after ENTRY_INTENT_TTL_MS so a stale intent from a long-ago invite link cannot
 * keep re-driving onboarding routing (see entryIntentExpiry.helper).
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { platformStorage } from '../../shared/stores/platformStorage';
import { logger } from '../../helpers/logger.helper';
import type { EntryIntent } from '../types/entryIntent.types';
import { shouldExpireEntryIntent } from '../helpers/entryIntentExpiry.helper';

interface EntryIntentState {
  intent: EntryIntent | null;
  /** Epoch ms when `intent` was last set — drives TTL expiry on rehydrate. */
  intentSavedAt: number | null;
  _hasHydrated: boolean;
  setIntent: (intent: EntryIntent) => void;
  patchIntent: (patch: Partial<EntryIntent>) => void;
  clearIntent: () => void;
}

export const useEntryIntentStore = create<EntryIntentState>()(
  persist(
    (set, get) => ({
      intent: null,
      intentSavedAt: null,
      _hasHydrated: false,

      setIntent: (intent) => set({ intent, intentSavedAt: Date.now() }),

      patchIntent: (patch) => {
        const current = get().intent;
        if (!current) return;
        // Narrow per kind: only merge keys valid for the current kind. Keep the original
        // savedAt — a patch (e.g. resolving a groupId) refines the same intent, it does not
        // restart its lifetime.
        set({ intent: { ...current, ...patch } as EntryIntent });
      },

      clearIntent: () => set({ intent: null, intentSavedAt: null }),
    }),
    {
      name: 'zporti_entry_intent',
      storage: createJSONStorage(() => platformStorage),
      partialize: (state) => ({ intent: state.intent, intentSavedAt: state.intentSavedAt }),
      onRehydrateStorage: () => (_state, error) => {
        if (error) {
          logger.error('Failed to hydrate entry intent store:', error);
        }
        queueMicrotask(() => {
          const { intent, intentSavedAt } = useEntryIntentStore.getState();
          if (shouldExpireEntryIntent(intent, intentSavedAt, Date.now())) {
            logger.debug('Discarding stale/expired entry intent on rehydrate');
            useEntryIntentStore.setState({ intent: null, intentSavedAt: null, _hasHydrated: true });
          } else {
            useEntryIntentStore.setState({ _hasHydrated: true });
          }
        });
      },
    },
  ),
);
